import { useFetcher } from '@remix-run/react'
import { useTranslation } from 'react-i18next'

import { Form } from '~/helpers/form'
import { makeShopOwnPath } from '~/helpers/routes'
import { useOptionalSeller } from '~/hooks/data/use-seller'
import { useShop } from '~/hooks/data/use-shop'
import { patchShopSchema } from '~/schemas/shop'
import { Alert } from '~/ui'
import { TextEditable } from '~/ui/text-editable'
import { cn } from '~/utils/classnames'

const style = '@sm:text-base text-foreground/64 text-sm leading-snug'
const layout = 'w-full lg:max-w-2/3'

export const ShopDescription = () => {
  const seller = useOptionalSeller()
  const shop = useShop()

  if (seller) {
    return <ShopDescriptionEditable />
  }

  return <p className={cn(style, layout)}>{shop.description}</p>
}

const ShopDescriptionEditable = () => {
  const { t } = useTranslation()
  const shop = useShop()
  const fetcher = useFetcher()

  const isLoading = fetcher.state === 'submitting'
  const fetcherData = fetcher.data as {
    error?: string
    errors?: string[]
  } | null
  const hasApiError =
    fetcherData?.error || (fetcherData?.errors?.length ?? 0) > 0

  return (
    <Form
      action={makeShopOwnPath(shop, '/settings?index')}
      method="PATCH"
      schema={patchShopSchema.pick({ description: true })}
      fetcher={fetcher}
      mode="onChange"
      values={{
        description: shop.description,
      }}
    >
      {({ Field, register, submit, getFieldState }) => {
        const fieldState = getFieldState('description')

        const errorMessage = fieldState.error?.message
          ? t(fieldState.error.message)
          : hasApiError
            ? t('errors.there_was_an_error')!
            : undefined

        return (
          <Field name="description" className={layout}>
            {() => (
              <div className="flex flex-col">
                <TextEditable className={cn(isLoading && 'animate-pulse')}>
                  <TextEditable.Textarea
                    className={style}
                    maxLength={120}
                    {...register('description')}
                    invalid={!!hasApiError || !!fieldState.error?.message}
                    onBlur={submit}
                    placeholder={t('input.shop_description.placeholder')!}
                  />
                </TextEditable>
                {errorMessage && (
                  <Alert type="error" className="my-1" title={errorMessage} />
                )}
              </div>
            )}
          </Field>
        )
      }}
    </Form>
  )
}
